import React from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { BiArrowBack, BiDotsVerticalRounded } from 'react-icons/bi';
import {
  MdCalendarToday,
  MdContentCopy,
  MdDirectionsCar,
  MdEdit,
  MdEventRepeat,
  MdNotes,
  MdOpenInNew,
  MdOutlineCancel,
  MdPerson,
  MdPhone,
  MdPlace,
  MdRoute,
  MdSend,
} from 'react-icons/md';

import { Appointments, Locations } from '@gql/base-types';
import { useUpdateAppointmentMutation } from '../queries/updateAppointment.mutation.generated';
import { useUpdateAppointmentIfNewMutation } from '../queries/updateAppointmentIfNew.mutation.generated';
import { useGetAllLocationsQuery } from '../../locations/queries/getAllLocations.query.generated';
import { copyToClipboard } from '@services/utilityService';
import { validatePhone, validateVin } from '@services/validationService';
import { useRooftop, useUser } from '@store';
import { MovePlannerLocationSelect } from '@hopdrive/feature-move-planner';
import { capabilityGate } from '@hopdrive/feature-portal-products';
import { useProductEntry } from '@features/products/useProductEntry';
import { useReloadGuard } from '@hopdrive/sdk-app-updater/react';

import { formatAppointmentType } from '../utils/appointmentType';
import {
  buildCardChanges,
  buildDraft,
  cardEquals,
  formatPhoneProgressive,
  formatWhen,
  CARD_IDS,
  type CardId,
  type Draft,
} from './appointmentDetailModel';

// Local shape for the service_orders → stop → run traversal on the
// GetAppointment subscription. Declared inline so this code stays type-safe
// even before codegen regenerates the colocated types with the new selection
// set; the runtime payload from Hasura already includes these fields.
type LinkedServiceOrder = {
  id: number;
  status?: string | null;
  vehicle_make?: string | null;
  vehicle_model?: string | null;
  vehicle_year?: number | null;
  vehicle_vin?: string | null;
  service_description?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  stop?: {
    id: number;
    stop_order?: number | null;
    status?: string | null;
    address?: string | null;
    run?: {
      id: number;
      status?: string | null;
      departure_time?: string | null;
      stops?: { id: number; stop_order?: number | null; status?: string | null }[] | null;
      driver?: {
        id: number;
        user?: { id: number; display_name?: string | null } | null;
      } | null;
    } | null;
  } | null;
};

const RUN_STATUS_COLOR: Record<string, 'default' | 'success' | 'warning' | 'info' | 'error'> = {
  draft: 'default',
  scheduled: 'info',
  dispatched: 'warning',
  claimed: 'warning',
  in_transit: 'info',
  in_progress: 'info',
  completed: 'success',
  canceled: 'error',
  cancelled: 'error',
};

const STATUS_CHIPS: Record<string, { label: string; color: 'default' | 'success' | 'warning' | 'info' | 'error' }> = {
  new: { label: 'New', color: 'info' },
  pending: { label: 'Pending', color: 'warning' },
  ready: { label: 'Ready', color: 'success' },
  scheduled: { label: 'Scheduled', color: 'info' },
  in_progress: { label: 'In Progress', color: 'warning' },
  completed: { label: 'Completed', color: 'success' },
  canceled: { label: 'Canceled', color: 'error' },
  cancelled: { label: 'Cancelled', color: 'error' },
};

const STOP_STATUS_LABELS: Record<string, string> = {
  pending_confirmation: 'Awaiting confirmation',
  confirmed: 'Confirmed',
  in_transit_to: 'Technician en route',
  arrived: 'Technician arrived',
  in_service: 'In service',
  service_in_progress: 'In service',
  completed: 'Completed',
  skipped: 'Skipped',
};

// Run states where the technician is actively out — the live strip only makes
// sense while there is something live to say.
const ACTIVE_RUN_STATUSES = ['dispatched', 'claimed', 'in_transit'];

// The coordinator-entered per-visit durations offered by the Service form.
const DURATION_OPTIONS = [15, 30, 45, 60, 90, 120];

const formatStatusWord = (status?: string | null) =>
  (status || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

/**
 * Best-effort split of the free-text dealer note into displayable items.
 * Notes commonly arrive as one line with '/'-separated segments
 * ("4TH GCM- OIL CHANGE & TIRE ROTATION / JARED JEWELRY"); newlines win when
 * present. Purely presentational — the raw note always renders underneath as
 * the source of truth.
 */
const parseServiceItems = (notes?: string | null): { text: string; tag?: string }[] => {
  if (!notes) return [];
  if (notes.includes('\n')) {
    return notes.split('\n').map(l => l.trim()).filter(Boolean).map(text => ({ text }));
  }
  // Single line: '/'-separated segments, the LAST one conventionally naming
  // the account ("... OIL CHANGE & TIRE ROTATION / JARED JEWELRY").
  const segments = notes.split(/\s*\/\s*/).map(l => l.trim()).filter(Boolean);
  const account = segments.length > 1 ? segments.pop()! : null;
  const items: { text: string; tag?: string }[] = segments
    .flatMap(seg => seg.split(/\s*&\s*/))
    .map(l => l.trim())
    .filter(Boolean)
    .map(text => ({ text }));
  if (account) items.push({ text: account, tag: 'Account' });
  return items;
};

const initials = (name?: string | null) =>
  (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0]!.toUpperCase())
    .join('') || '?';

/** Comp card chrome: soft shadow, no border — matches the mobile-service
 *  run-detail cards rather than MUI's outlined variant. */
const CARD_SX = {
  border: 'none',
  borderRadius: 2.5,
  boxShadow: '0px 4px 12px rgba(0,0,0,0.08), 0px 2px 4px rgba(0,0,0,0.04)',
} as const;

/** Status chip with the comp's leading dot. */
const DotChip: React.FC<{
  label: string;
  color: 'default' | 'success' | 'warning' | 'info' | 'error';
  variant?: 'filled' | 'outlined';
}> = ({ label, color, variant = 'filled' }) => (
  <Chip
    size='small'
    label={label}
    color={color}
    variant={variant}
    icon={<Box component='span' sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor', ml: '4px !important' }} />}
    sx={{ fontWeight: 600 }}
  />
);

/** Small uppercase card/section label with a leading icon. */
const SectionLabel: React.FC<{ icon: React.ReactNode; children: React.ReactNode; trailing?: React.ReactNode }> = ({
  icon,
  children,
  trailing,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
    <Box sx={{ display: 'flex', color: 'text.disabled' }}>{icon}</Box>
    <Typography variant='overline' sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 1, lineHeight: 1 }}>
      {children}
    </Typography>
    <Box sx={{ flex: 1 }} />
    {trailing}
  </Box>
);

/** External field label, matching the run-stop edit form's convention. */
const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <Box>
    <Typography variant='caption' sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
      {label}
    </Typography>
    {children}
    {hint ? (
      <Typography variant='caption' color='text.disabled' sx={{ display: 'block', mt: 0.5 }}>
        {hint}
      </Typography>
    ) : null}
  </Box>
);

/**
 * Banner text for an edit abandoned because the appointment left 'new'.
 * `statusLabel` is the status it moved to, as the chip shows it — or null
 * when the server refused the write before the subscription caught up.
 */
const staleEditMessage = (statusLabel: string | null, hadChanges: boolean) =>
  (statusLabel
    ? `This appointment moved to ${statusLabel} while you were editing, so `
    : 'This appointment is no longer New, so ') +
  (hadChanges ? 'your unsaved changes were not saved. ' : 'editing was closed. ') +
  'Details can only be edited while an appointment is New — once it is on a run, edit them from the run screen.';

// ── Draft model ──────────────────────────────────────────────────────────── //

interface Props {
  appointment: Appointments;
}

const MobileServiceAppointmentDetails: React.FC<Props> = ({ appointment }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const user = useUser();
  const rooftop = useRooftop();

  const [cancelOpen, setCancelOpen] = React.useState(false);
  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);
  const closeMenu = () => setMenuAnchor(null);
  const [updateAppointment, { loading: canceling }] = useUpdateAppointmentMutation();
  const [updateAppointmentIfNew, { loading: mutating }] = useUpdateAppointmentIfNewMutation();

  const statusKey = (appointment.status || '').toLowerCase();
  const statusCfg = STATUS_CHIPS[statusKey] || { label: appointment.status || 'Unknown', color: 'default' as const };
  // Cancel only makes sense while the appointment can still legally reach
  // 'canceled' — terminal states (completed, canceled, refunded, missed,
  // duplicate) must not offer it. Mirrors sdk-appointments' STATUS_TRANSITIONS.
  // Cancelling and inline editing are manage actions, so both need the Mobile Service
  // product's manage capability; a read_only product shows the appointment without them.
  const TERMINAL_STATUSES = ['canceled', 'cancelled', 'completed', 'refunded', 'missed', 'duplicate'];
  const canManage = capabilityGate(useProductEntry('mobile-service'), 'manageExisting').kind === 'render';
  const canCancel = !TERMINAL_STATUSES.includes(statusKey) && canManage;
  // Inline editing is a 'new'-only affordance: once the appointment is on a
  // run ('pending' and beyond), its details were copied to the run's stop and
  // edits here wouldn't propagate — run edit mode owns those.
  const editable = statusKey === 'new' && canManage;

  // ── Inline edit state ── //
  const savedDraft = React.useMemo(() => buildDraft(appointment), [appointment]);
  const [draft, setDraft] = React.useState<Draft>(savedDraft);
  const [editing, setEditing] = React.useState<Set<CardId>>(new Set());
  const [editAll, setEditAll] = React.useState(false);
  const [fieldErrors, setFieldErrors] = React.useState<{ phone?: string; vin?: string; location?: string }>({});
  // Set when an in-progress edit is abandoned because the appointment left
  // 'new' underneath it (a colleague dispatched a run). Dismissible banner.
  const [staleEditNotice, setStaleEditNotice] = React.useState<string | null>(null);

  // A different appointment (or a server-side update while NOT editing)
  // re-seeds the draft. Mid-edit, the local draft wins until save/cancel.
  React.useEffect(() => {
    if (editing.size === 0) setDraft(savedDraft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedDraft]);
  React.useEffect(() => {
    setDraft(buildDraft(appointment));
    setEditing(new Set());
    setEditAll(false);
    setFieldErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointment.id]);

  // The subscription can move the status out of 'new' while cards are open.
  // Editing is then no longer legal, so discard the draft, close the cards
  // and say why — rather than leaving a Pending chip above live edit forms.
  React.useEffect(() => {
    if (editable || editing.size === 0) return;
    const hadChanges = CARD_IDS.some(id => !cardEquals(id, draft, savedDraft));
    setDraft(savedDraft);
    setEditing(new Set());
    setEditAll(false);
    setFieldErrors({});
    setStaleEditNotice(staleEditMessage(statusCfg.label, hadChanges));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable]);
  React.useEffect(() => setStaleEditNotice(null), [appointment.id]);

  const dirtyCards = CARD_IDS.filter(id => !cardEquals(id, draft, savedDraft));
  const isEditing = (id: CardId) => editing.has(id);
  // Only cards still open count: after a save the draft differs from the row
  // until the subscription catches up, but that card is already closed.
  const holdsUnsavedWork = mutating || dirtyCards.some(id => editing.has(id));
  useReloadGuard(holdsUnsavedWork, 'Appointment details');

  const enterEdit = (id: CardId) => setEditing(prev => new Set(prev).add(id));
  const cancelEdit = (id: CardId) => {
    setDraft(prev => ({ ...prev, [id]: savedDraft[id] } as Draft));
    setEditing(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setFieldErrors({});
  };
  const enterEditAll = () => {
    setEditAll(true);
    setEditing(new Set(CARD_IDS));
  };
  const cancelEditAll = () => {
    setDraft(savedDraft);
    setEditAll(false);
    setEditing(new Set());
    setFieldErrors({});
  };

  // Saved/favorite locations for the picker — fetched only once editing starts.
  const contractCustomerId = rooftop?.id || user?.profile?.customer_id || 0;
  const { data: locationsData, refetch: refetchLocations } = useGetAllLocationsQuery({
    variables: { customerId: contractCustomerId },
    skip: !contractCustomerId || !isEditing('location'),
    fetchPolicy: 'cache-and-network',
  });
  const savedLocations = (locationsData?.locations ?? []) as (Locations | null)[];

  /** Drop the open edit, close the cards and explain — the appointment left 'new'. */
  const abandonStaleEdit = (statusLabel: string | null) => {
    const hadChanges = CARD_IDS.some(id => !cardEquals(id, draft, savedDraft));
    cancelEditAll();
    const message = staleEditMessage(statusLabel, hadChanges);
    setStaleEditNotice(message);
    toast.error('Not saved — this appointment is no longer New');
  };

  // ── Save: one status-guarded updateAppointment carrying the dirty cards' columns ── //
  const saveCards = async (ids: CardId[]) => {
    const toSave = ids.filter(id => !cardEquals(id, draft, savedDraft));
    if (toSave.length === 0) return;

    // Optional-but-valid gates, mirroring the appointment dialog.
    const errors: typeof fieldErrors = {};
    const phoneDigits = draft.consumer.phone.replace(/\D/g, '');
    if (toSave.includes('consumer') && phoneDigits && !validatePhone(phoneDigits)) {
      errors.phone = 'Enter a valid 10-digit phone number';
    }
    if (toSave.includes('vehicle') && draft.vehicle.vin.trim() && !validateVin(draft.vehicle.vin.trim())) {
      errors.vin = 'Enter a valid 17-character VIN';
    }
    if (toSave.includes('location') && !draft.location?.id) {
      errors.location = 'Pick a service address';
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    // Last-seen status already left 'new' (the effect above normally closes
    // the cards first; this covers a click that lands in the same tick).
    if (!editable) {
      abandonStaleEdit(statusCfg.label);
      return;
    }

    const changes: Record<string, unknown> = {
      updatedat: new Date().toISOString(),
      updatedby: user?.profile?.email || 'unknown',
      ...buildCardChanges(toSave, draft, appointment),
    };

    try {
      const result = await updateAppointmentIfNew({ variables: { id: appointment.id, changes: changes as any } });
      const written = result.data?.update_appointments;
      if (!written || written.affected_rows === 0) {
        // The row was no longer 'new' by the time the write ran — the
        // status condition refused it. Nothing was written.
        abandonStaleEdit(null);
        return;
      }
      toast.success(toSave.length === 1 ? 'Appointment updated' : `${toSave.length} sections saved`);
      setEditing(prev => {
        const next = new Set(prev);
        toSave.forEach(id => next.delete(id));
        return next;
      });
      if (editAll) {
        setEditAll(false);
        setEditing(new Set());
      }
      setFieldErrors({});
    } catch (err: any) {
      toast.error(`Failed to save: ${err?.message || 'Unknown error'}`);
    }
  };

  const vehicleLabel = [draft.vehicle.year, draft.vehicle.make, draft.vehicle.model].filter(Boolean).join(' ');
  const when = formatWhen(appointment.appointment_time);
  const roNumber = savedDraft.vehicle.ro || null;
  const estimatedMinutes = Number(savedDraft.service.minutes) || null;
  const serviceItems = parseServiceItems(appointment.driver_notes);
  const savedPhoneDigits = (appointment.consumer_phone || '').replace(/\D/g, '');

  // ── Linked run(s) via service_orders.appointment_id ── //
  const linkedOrders = ((appointment as { service_orders?: LinkedServiceOrder[] }).service_orders ?? []);
  // One appointment begets one service order, so runMap is a dedup rather
  // than a grouping — the run card renders run/stop state only.
  const runMap = new Map<
    number,
    { run: NonNullable<NonNullable<LinkedServiceOrder['stop']>['run']>; stop: NonNullable<LinkedServiceOrder['stop']> }
  >();
  for (const order of linkedOrders) {
    const run = order.stop?.run;
    const stop = order.stop;
    if (!run || !stop) continue;
    if (!runMap.has(run.id)) runMap.set(run.id, { run, stop });
  }
  const runEntries = Array.from(runMap.values());
  const primaryEntry = runEntries[0] ?? null;

  const handleConfirmCancel = async () => {
    try {
      await updateAppointment({
        variables: {
          id: appointment.id,
          changes: {
            status: 'canceled',
            updatedat: new Date().toISOString(),
            updatedby: user?.profile?.email || 'unknown',
          } as any,
        },
      });
      toast.success('Appointment canceled');
      setCancelOpen(false);
    } catch (err: any) {
      toast.error(`Failed to cancel appointment: ${err?.message || 'Unknown error'}`);
    }
  };

  const handleCopy = (value: string, what: string) => {
    copyToClipboard(value);
    toast.success(`${what} copied`);
  };

  const goToRun = (runId: number) => navigate(`/mobile-service/runs/${runId}`);

  const mapsHref = appointment.pickup?.address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(appointment.pickup.address)}`
    : null;

  // ── Live strip: only while a linked run is actively out ── //
  const liveEntry =
    primaryEntry && ACTIVE_RUN_STATUSES.includes(primaryEntry.run.status ?? '') ? primaryEntry : null;
  const liveDriverFirst = liveEntry?.run.driver?.user?.display_name?.split(/\s+/)[0] ?? 'The technician';
  const liveStopCount = liveEntry?.run.stops?.length ?? 0;
  const liveStopLabel =
    liveEntry?.stop.status ? STOP_STATUS_LABELS[liveEntry.stop.status] ?? formatStatusWord(liveEntry.stop.status) : null;

  // Why editing is unavailable, for the menu hint + lock note.
  const lockReason = editable
    ? null
    : !canManage
      ? 'Mobile Service is view-only for this rooftop.'
      : statusKey === 'completed'
      ? "This appointment is complete. Details are read-only."
      : TERMINAL_STATUSES.includes(statusKey)
        ? 'This appointment is closed. Details are read-only.'
        : 'This appointment is on a run — edit its details from the run screen.';

  /** Pencil button shown on hover while the card is viewable + editable. */
  const editPencil = (id: CardId) =>
    editable && !isEditing(id) ? (
      <IconButton
        size='small'
        className='card-edit-btn'
        aria-label={`Edit ${id}`}
        onClick={() => enterEdit(id)}
        sx={{ opacity: 0, transition: 'opacity 0.15s', p: 0.25 }}
      >
        <MdEdit size={15} />
      </IconButton>
    ) : null;

  /** Per-card Cancel/Save footer (hidden in edit-all mode — the editbar owns it). */
  const cardFooter = (id: CardId) =>
    !editAll ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.75, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant='caption' color='text.secondary' sx={{ flex: 1 }}>
          {cardEquals(id, draft, savedDraft) ? '' : 'Unsaved changes'}
        </Typography>
        <Button size='small' variant='outlined' onClick={() => cancelEdit(id)} sx={{ textTransform: 'none', fontWeight: 600 }}>
          Cancel
        </Button>
        <Button
          size='small'
          variant='contained'
          disabled={mutating || cardEquals(id, draft, savedDraft)}
          onClick={() => saveCards([id])}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Save
        </Button>
      </Box>
    ) : null;

  /** Card wrapper: hover reveals the pencil; editing shows the accent bar. */
  const cardSx = (id: CardId) => ({
    ...CARD_SX,
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    p: 2,
    position: 'relative' as const,
    overflow: 'visible' as const,
    '&:hover .card-edit-btn, &:focus-within .card-edit-btn': { opacity: 1 },
    ...(isEditing(id)
      ? {
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 10,
            bottom: 10,
            width: 3,
            borderRadius: '0 3px 3px 0',
            bgcolor: 'primary.main',
          },
        }
      : {}),
  });

  return (
    <Container maxWidth='lg' sx={{ py: 3 }}>
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
        <IconButton onClick={() => navigate(-1)} size='small' aria-label='Back' sx={{ mt: 0.25 }}>
          <BiArrowBack />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant='h5' sx={{ fontWeight: 600 }}>
              Mobile service appointment #{appointment.id}
            </Typography>
            <Chip label={formatAppointmentType(appointment.type)} size='small' variant='outlined' sx={{ fontWeight: 600 }} />
            <DotChip label={statusCfg.label} color={statusCfg.color} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.75 }}>
            <Typography variant='body2' sx={{ fontWeight: 600 }}>
              {when.primary}
            </Typography>
            {when.secondary ? (
              <>
                <Typography variant='body2' color='text.disabled'>·</Typography>
                <Typography variant='body2' color='text.secondary'>
                  {when.secondary}
                </Typography>
              </>
            ) : null}
            <Typography variant='body2' color='text.disabled'>·</Typography>
            <Box sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              bgcolor: 'action.hover',
              borderRadius: 1,
              pl: 1,
              pr: roNumber ? 0.25 : 1,
              py: 0.25,
            }}>
              <Typography variant='caption' sx={{ fontWeight: 700, letterSpacing: 0.5, color: 'text.secondary' }}>
                RO
              </Typography>
              {roNumber ? (
                <>
                  <Typography variant='caption' sx={{ fontFamily: 'monospace', fontWeight: 600, letterSpacing: 0.3 }}>
                    {roNumber}
                  </Typography>
                  <IconButton size='small' aria-label='Copy RO number' onClick={() => handleCopy(roNumber, 'RO number')}>
                    <MdContentCopy size={12} />
                  </IconButton>
                </>
              ) : (
                <Typography variant='caption' color='text.disabled' sx={{ fontStyle: 'italic' }}>
                  none
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {primaryEntry ? (
            <Button
              size='small'
              variant='outlined'
              startIcon={<MdOpenInNew size={14} />}
              onClick={() => goToRun(primaryEntry.run.id)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              View run
            </Button>
          ) : null}
          <IconButton
            size='small'
            aria-label='More actions'
            onClick={e => setMenuAnchor(e.currentTarget)}
          >
            <BiDotsVerticalRounded size={20} />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={closeMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem
              disabled={!editable}
              onClick={() => {
                closeMenu();
                enterEditAll();
              }}
            >
              <ListItemIcon><MdEdit size={18} /></ListItemIcon>
              <ListItemText primary='Edit appointment' />
            </MenuItem>
            {lockReason ? (
              <Typography variant='caption' color='text.disabled' sx={{ display: 'block', px: 2, pb: 0.75, maxWidth: 260, lineHeight: 1.4 }}>
                {lockReason}
              </Typography>
            ) : null}
            {/* Mocked pending real flows — disabled rather than firing a fake
                success (a "resent" toast that texts nobody would mislead). */}
            <Tooltip title='Coming soon' placement='left'>
              <span>
                <MenuItem disabled>
                  <ListItemIcon><MdEventRepeat size={18} /></ListItemIcon>
                  <ListItemText primary='Reschedule' />
                </MenuItem>
              </span>
            </Tooltip>
            <Tooltip title='Coming soon' placement='left'>
              <span>
                <MenuItem disabled>
                  <ListItemIcon><MdSend size={18} /></ListItemIcon>
                  <ListItemText primary='Resend confirmation' />
                </MenuItem>
              </span>
            </Tooltip>
            {canCancel ? <Divider /> : null}
            {canCancel ? (
              <MenuItem
                onClick={() => {
                  closeMenu();
                  setCancelOpen(true);
                }}
                sx={{ color: 'error.main' }}
              >
                <ListItemIcon sx={{ color: 'error.main' }}><MdOutlineCancel size={18} /></ListItemIcon>
                <ListItemText primary='Cancel appointment' />
              </MenuItem>
            ) : null}
          </Menu>
        </Box>
      </Box>

      {/* ── Live strip (only while the run is actively out) ── */}
      {liveEntry ? (
        <Card
          elevation={0}
          sx={{
            ...CARD_SX,
            display: 'flex',
            alignItems: 'center',
            gap: 1.75,
            px: 2,
            py: 1.5,
            mb: 2,
            borderLeft: 3,
            borderLeftColor: 'info.main',
            borderLeftStyle: 'solid',
            overflow: 'visible',
          }}
        >
          <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: 'info.light', color: 'info.dark' }}>
              <MdDirectionsCar size={18} />
            </Avatar>
            {/* comp's ping ring */}
            <Box sx={{
              position: 'absolute',
              inset: -4,
              borderRadius: '50%',
              border: 2,
              borderColor: 'info.main',
              borderStyle: 'solid',
              opacity: 0,
              animation: 'appt-ping 2.4s cubic-bezier(0, 0, 0.2, 1) infinite',
              '@keyframes appt-ping': {
                '0%': { transform: 'scale(0.85)', opacity: 0.6 },
                '80%, 100%': { transform: 'scale(1.25)', opacity: 0 },
              },
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant='body2' sx={{ fontWeight: 600 }}>
              {liveDriverFirst} is {formatStatusWord(liveEntry.run.status).toLowerCase()}
              {liveEntry.stop.stop_order != null && liveStopCount > 0
                ? ` — this is stop ${liveEntry.stop.stop_order} of ${liveStopCount}`
                : ''}
            </Typography>
            {liveStopLabel ? (
              <Typography variant='caption' color='text.secondary'>
                {liveStopLabel}
              </Typography>
            ) : null}
          </Box>
        </Card>
      ) : null}

      {staleEditNotice ? (
        <Alert severity='warning' onClose={() => setStaleEditNotice(null)} sx={{ mb: 2 }}>
          {staleEditNotice}
        </Alert>
      ) : null}

      {/* ── Detail cards ── */}
      <Grid container spacing={2}>
        {/* Consumer */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card elevation={0} sx={cardSx('consumer')}>
            <SectionLabel icon={<MdPerson size={16} />} trailing={editPencil('consumer')}>
              Consumer
            </SectionLabel>
            {isEditing('consumer') ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Field label='Name'>
                  <TextField
                    size='small'
                    fullWidth
                    value={draft.consumer.name}
                    onChange={e => setDraft(d => ({ ...d, consumer: { ...d.consumer, name: e.target.value } }))}
                  />
                </Field>
                <Field label='Phone'>
                  <TextField
                    size='small'
                    fullWidth
                    value={draft.consumer.phone}
                    error={Boolean(fieldErrors.phone)}
                    helperText={fieldErrors.phone}
                    slotProps={{ htmlInput: { maxLength: 14, inputMode: 'tel' } }}
                    onChange={e =>
                      setDraft(d => ({ ...d, consumer: { ...d.consumer, phone: formatPhoneProgressive(e.target.value) } }))
                    }
                  />
                </Field>
                {cardFooter('consumer')}
              </Box>
            ) : (
              <>
                <Typography variant='h6' sx={{ fontWeight: 600 }}>
                  {appointment.consumer_name || '—'}
                </Typography>
                <Typography variant='body2' color={appointment.consumer_phone ? 'text.secondary' : 'text.disabled'} sx={{ mt: 0.25 }}>
                  {formatPhoneProgressive(appointment.consumer_phone || '') || 'No phone number'}
                </Typography>
                {savedPhoneDigits ? (
                  <Box sx={{ display: 'flex', gap: 1, mt: 'auto', pt: 1.5 }}>
                    <Button size='small' variant='outlined' startIcon={<MdPhone size={14} />} href={`tel:${savedPhoneDigits}`}
                      sx={{ textTransform: 'none', fontWeight: 600 }}>
                      Call
                    </Button>
                  </Box>
                ) : null}
              </>
            )}
          </Card>
        </Grid>

        {/* Service location */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card elevation={0} sx={cardSx('location')}>
            <SectionLabel icon={<MdPlace size={16} />} trailing={editPencil('location')}>
              Service location
            </SectionLabel>
            {isEditing('location') ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <MovePlannerLocationSelect
                  rooftopId={contractCustomerId}
                  location={draft.location}
                  locations={savedLocations}
                  onLocationChange={(loc?: Locations | null) => setDraft(d => ({ ...d, location: loc || null }))}
                  onLocationFavorite={() => {
                    refetchLocations();
                  }}
                  label='Service Address'
                  placeholder='Search saved locations or an address...'
                  helperText={fieldErrors.location ?? 'Favorites appear first — or search for a new address'}
                  required
                />
                {cardFooter('location')}
              </Box>
            ) : appointment.pickup ? (
              <>
                <Typography variant='h6' sx={{ fontWeight: 600 }}>
                  {appointment.pickup.name || appointment.pickup.address || '—'}
                </Typography>
                {appointment.pickup.name && appointment.pickup.address ? (
                  <Typography variant='body2' color='text.secondary' sx={{ mt: 0.25 }}>
                    {appointment.pickup.address}
                  </Typography>
                ) : null}
                <Box sx={{ display: 'flex', gap: 1, mt: 'auto', pt: 1.5 }}>
                  {mapsHref ? (
                    <Button size='small' variant='outlined' startIcon={<MdOpenInNew size={14} />} href={mapsHref}
                      target='_blank' rel='noreferrer' sx={{ textTransform: 'none', fontWeight: 600 }}>
                      Open in Maps
                    </Button>
                  ) : null}
                  {appointment.pickup.address ? (
                    <Button size='small' variant='outlined' startIcon={<MdContentCopy size={14} />}
                      onClick={() => handleCopy(appointment.pickup!.address!, 'Address')}
                      sx={{ textTransform: 'none', fontWeight: 600 }}>
                      Copy
                    </Button>
                  ) : null}
                </Box>
              </>
            ) : (
              <Typography variant='body2' color='text.disabled'>
                No service address on file
              </Typography>
            )}
          </Card>
        </Grid>

        {/* Vehicle */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card elevation={0} sx={cardSx('vehicle')}>
            <SectionLabel icon={<MdDirectionsCar size={16} />} trailing={editPencil('vehicle')}>
              Vehicle
            </SectionLabel>
            {isEditing('vehicle') ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Box sx={{ width: 90, flexShrink: 0 }}>
                    <Field label='Year'>
                      <TextField
                        size='small'
                        fullWidth
                        value={draft.vehicle.year}
                        slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 4 } }}
                        onChange={e => setDraft(d => ({ ...d, vehicle: { ...d.vehicle, year: e.target.value } }))}
                      />
                    </Field>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Field label='Make'>
                      <TextField
                        size='small'
                        fullWidth
                        value={draft.vehicle.make}
                        onChange={e => setDraft(d => ({ ...d, vehicle: { ...d.vehicle, make: e.target.value } }))}
                      />
                    </Field>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Field label='Model'>
                      <TextField
                        size='small'
                        fullWidth
                        value={draft.vehicle.model}
                        onChange={e => setDraft(d => ({ ...d, vehicle: { ...d.vehicle, model: e.target.value } }))}
                      />
                    </Field>
                  </Box>
                </Box>
                <Field label='VIN' hint='17 characters · no I, O, or Q'>
                  <TextField
                    size='small'
                    fullWidth
                    value={draft.vehicle.vin}
                    error={Boolean(fieldErrors.vin)}
                    helperText={fieldErrors.vin}
                    slotProps={{ htmlInput: { maxLength: 17, style: { fontFamily: 'monospace', letterSpacing: 0.4 } } }}
                    onChange={e => setDraft(d => ({ ...d, vehicle: { ...d.vehicle, vin: e.target.value.toUpperCase() } }))}
                  />
                </Field>
                <Field label='RO number' hint='Dealer-side key · not shown to the customer'>
                  <TextField
                    size='small'
                    fullWidth
                    placeholder='Not assigned'
                    value={draft.vehicle.ro}
                    slotProps={{ htmlInput: { style: { fontFamily: 'monospace', letterSpacing: 0.4 } } }}
                    onChange={e => setDraft(d => ({ ...d, vehicle: { ...d.vehicle, ro: e.target.value } }))}
                  />
                </Field>
                {cardFooter('vehicle')}
              </Box>
            ) : (
              <>
                {vehicleLabel ? (
                  <Typography variant='h6' sx={{ fontWeight: 600 }}>
                    {vehicleLabel}
                  </Typography>
                ) : (
                  <Typography variant='body2' color='text.disabled'>
                    No vehicle on file
                  </Typography>
                )}
                {appointment.vehicle_vin ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <Typography variant='caption' color='text.secondary' sx={{ minWidth: 28 }}>
                      VIN
                    </Typography>
                    <Typography variant='caption' sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', px: 0.75, py: 0.25, borderRadius: 0.5, letterSpacing: 0.4 }}>
                      {appointment.vehicle_vin}
                    </Typography>
                    <IconButton size='small' aria-label='Copy VIN' onClick={() => handleCopy(appointment.vehicle_vin!, 'VIN')}>
                      <MdContentCopy size={12} />
                    </IconButton>
                  </Box>
                ) : null}
                {roNumber ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
                    <Typography variant='caption' color='text.secondary' sx={{ minWidth: 28 }}>
                      RO
                    </Typography>
                    <Typography variant='caption' sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', px: 0.75, py: 0.25, borderRadius: 0.5, letterSpacing: 0.4 }}>
                      {roNumber}
                    </Typography>
                  </Box>
                ) : null}
              </>
            )}
          </Card>
        </Grid>

        {/* Service requested */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card elevation={0} sx={cardSx('service')}>
            <SectionLabel
              icon={<MdNotes size={16} />}
              trailing={
                <>
                  {!isEditing('service') && estimatedMinutes ? (
                    <Typography variant='caption' sx={{ fontWeight: 600, color: 'text.secondary' }}>
                      Est. {estimatedMinutes} min
                    </Typography>
                  ) : null}
                  {editPencil('service')}
                </>
              }
            >
              Service requested
            </SectionLabel>
            {isEditing('service') ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Field label='Scheduled time'>
                  <TextField
                    size='small'
                    fullWidth
                    type='datetime-local'
                    value={draft.service.time}
                    onChange={e => setDraft(d => ({ ...d, service: { ...d.service, time: e.target.value } }))}
                  />
                </Field>
                <Field label='Service notes'>
                  <TextField
                    size='small'
                    fullWidth
                    multiline
                    minRows={3}
                    value={draft.service.notes}
                    onChange={e => setDraft(d => ({ ...d, service: { ...d.service, notes: e.target.value } }))}
                  />
                </Field>
                <Field label='Estimated duration'>
                  <Select
                    size='small'
                    fullWidth
                    value={DURATION_OPTIONS.includes(Number(draft.service.minutes)) ? draft.service.minutes : String(DURATION_OPTIONS[1])}
                    onChange={e => setDraft(d => ({ ...d, service: { ...d.service, minutes: String(e.target.value) } }))}
                  >
                    {DURATION_OPTIONS.map(v => (
                      <MenuItem key={v} value={String(v)}>
                        {v < 60 ? `${v} min` : `${v / 60} hr${v % 60 ? ` ${v % 60} min` : ''}`}
                      </MenuItem>
                    ))}
                  </Select>
                </Field>
                {cardFooter('service')}
              </Box>
            ) : (
              <>
                {serviceItems.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {serviceItems.map((item, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: 'text.disabled', mt: '7px', flexShrink: 0 }} />
                        <Typography variant='body2'>
                          {item.tag ? (
                            <Box component='span' sx={{
                              fontSize: 10,
                              fontWeight: 700,
                              letterSpacing: 0.4,
                              textTransform: 'uppercase',
                              bgcolor: 'action.selected',
                              color: 'text.secondary',
                              px: 0.75,
                              py: 0.25,
                              borderRadius: 0.5,
                              mr: 0.75,
                              verticalAlign: '1px',
                            }}>
                              {item.tag}
                            </Box>
                          ) : null}
                          {item.text}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant='body2' color='text.disabled'>
                    No notes
                  </Typography>
                )}
                {appointment.driver_notes ? (
                  <Typography variant='caption' color='text.disabled' sx={{
                    display: 'block',
                    mt: 1.5,
                    pt: 1,
                    borderTop: 1,
                    borderColor: 'divider',
                    borderTopStyle: 'dashed',
                    fontFamily: 'monospace',
                    wordBreak: 'break-word',
                  }}>
                    Dealer note: {appointment.driver_notes}
                  </Typography>
                ) : null}
              </>
            )}
          </Card>
        </Grid>
      </Grid>

      {/* ── Edit-all bar (sticky) ── */}
      {editAll ? (
        <Box sx={{
          position: 'sticky',
          bottom: 12,
          zIndex: 20,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          mt: 2,
          px: 2,
          py: 1.5,
          borderRadius: 2.5,
          bgcolor: '#202430',
          color: '#fff',
          boxShadow: '0px 2px 16px 2px rgba(32,36,48,0.28)',
        }}>
          <Typography variant='body2' sx={{ fontWeight: 600, flex: 1 }}>
            {dirtyCards.length > 0
              ? `${dirtyCards.length} section${dirtyCards.length === 1 ? '' : 's'} changed`
              : 'Editing appointment — no changes yet'}
          </Typography>
          <Button
            size='small'
            onClick={cancelEditAll}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#fff', borderColor: '#ffffff38' }}
            variant='outlined'
          >
            Cancel
          </Button>
          <Button
            size='small'
            variant='contained'
            disabled={mutating || dirtyCards.length === 0}
            onClick={() => saveCards(CARD_IDS)}
            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#fff', color: '#202430', '&:hover': { bgcolor: '#f1f2f3' } }}
          >
            Save changes
          </Button>
        </Box>
      ) : null}

      {/* ── Associated run(s) ── */}
      <Box sx={{ mt: 3 }}>
        <SectionLabel icon={<MdRoute size={16} />}>Associated run</SectionLabel>

        {runEntries.length === 0 ? (
          <Card elevation={0} sx={{ ...CARD_SX, display: 'flex', alignItems: 'center', gap: 1.5, p: 2 }}>
            <Avatar variant='rounded' sx={{ width: 38, height: 38, bgcolor: 'action.hover', color: 'text.disabled' }}>
              <MdRoute size={18} />
            </Avatar>
            <Box>
              <Typography variant='body2' sx={{ fontWeight: 600 }}>
                Not yet assigned to a run
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                Once it's placed on a run, driver and stop details appear here.
              </Typography>
            </Box>
          </Card>
        ) : (
          runEntries.map(({ run, stop }) => {
            const chipColor = RUN_STATUS_COLOR[run.status ?? ''] ?? 'default';
            const driverName = run.driver?.user?.display_name ?? null;
            const runStops = (run.stops ?? []).slice().sort((a, b) => (a.stop_order ?? 0) - (b.stop_order ?? 0));
            const completedCount = runStops.filter(s => s.status === 'completed' || s.status === 'skipped').length;
            const stopStatusLabel = stop.status
              ? STOP_STATUS_LABELS[stop.status] ?? formatStatusWord(stop.status)
              : null;
            return (
              <Card key={run.id} elevation={0} sx={{ ...CARD_SX, mb: 1.5, overflow: 'hidden' }}>
                {/* Run head */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
                    Run #{run.id}
                  </Typography>
                  {run.status && <DotChip label={formatStatusWord(run.status)} color={chipColor} />}
                  <Box sx={{ flex: 1 }} />
                  <Button
                    size='small'
                    variant='outlined'
                    startIcon={<MdOpenInNew size={14} />}
                    onClick={() => goToRun(run.id)}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    View run
                  </Button>
                </Box>

                {/* Run meta */}
                <Grid container spacing={1.5} sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant='caption' sx={{ fontWeight: 700, letterSpacing: 0.5, color: 'text.secondary', textTransform: 'uppercase', display: 'block' }}>
                      Departure
                    </Typography>
                    <Typography variant='body2' sx={{ mt: 0.25 }}>
                      {run.departure_time ? formatWhen(run.departure_time).primary : '—'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant='caption' sx={{ fontWeight: 700, letterSpacing: 0.5, color: 'text.secondary', textTransform: 'uppercase', display: 'block' }}>
                      Driver
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                      {driverName ? (
                        <Avatar sx={{ width: 24, height: 24, fontSize: 11, fontWeight: 600, bgcolor: 'secondaryAccent.main' }}>
                          {initials(driverName)}
                        </Avatar>
                      ) : null}
                      <Typography variant='body2'>{driverName ?? 'Unassigned'}</Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant='caption' sx={{ fontWeight: 700, letterSpacing: 0.5, color: 'text.secondary', textTransform: 'uppercase', display: 'block' }}>
                      Progress
                    </Typography>
                    <Typography variant='body2' sx={{ mt: 0.25 }}>
                      {runStops.length > 0 ? `${completedCount} of ${runStops.length} stops complete` : '—'}
                    </Typography>
                  </Grid>
                </Grid>

                {/* Stop strip with pips */}
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant='body2' sx={{ fontWeight: 600 }}>
                      This appointment is stop {stop.stop_order ?? '—'}
                    </Typography>
                    {stopStatusLabel ? (
                      <DotChip label={stopStatusLabel} color={stop.status === 'completed' ? 'success' : stop.status === 'pending_confirmation' ? 'warning' : 'info'} variant='outlined' />
                    ) : null}
                    <Box sx={{ flex: 1 }} />
                    {runStops.length > 0 ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {runStops.map((s, i) => {
                          const done = s.status === 'completed' || s.status === 'skipped';
                          const isThis = s.id === stop.id;
                          return (
                            <React.Fragment key={s.id}>
                              {i > 0 ? (
                                <Box sx={{ height: 2, width: 16, bgcolor: done ? 'success.main' : 'divider' }} />
                              ) : null}
                              <Box sx={{
                                width: 22,
                                height: 22,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 11,
                                fontWeight: 600,
                                flexShrink: 0,
                                bgcolor: done ? 'success.main' : isThis ? 'info.main' : 'action.hover',
                                color: done || isThis ? '#fff' : 'text.secondary',
                                boxShadow: isThis ? `0 0 0 3px ${theme.palette.info.light}40` : 'none',
                              }}>
                                {s.stop_order ?? i + 1}
                              </Box>
                            </React.Fragment>
                          );
                        })}
                      </Box>
                    ) : null}
                  </Box>
                  {stop.address ? (
                    <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                      {stop.address}
                    </Typography>
                  ) : null}

                </Box>
              </Card>
            );
          })
        )}
      </Box>

      {/* ── Audit footer ── */}
      <Divider sx={{ my: 3 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
        <MdCalendarToday size={14} />
        <Typography variant='caption'>
          Created {appointment.createdat ? dayjs(appointment.createdat).format('MMM D, YYYY · h:mm A') : '—'}
          {appointment.createdby ? ` by ${appointment.createdby}` : ''}
        </Typography>
      </Box>

      {/* ── Cancel confirmation ── */}
      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth='xs' fullWidth>
        <DialogTitle>Cancel appointment?</DialogTitle>
        <DialogContent>
          <Typography variant='body2'>
            This will mark appointment #{appointment.id} as canceled. The customer will not be notified automatically.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)} sx={{ textTransform: 'none' }}>
            Keep It
          </Button>
          <Button
            variant='contained'
            color='error'
            onClick={handleConfirmCancel}
            disabled={canceling}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {canceling ? <><CircularProgress size={16} color='inherit' sx={{ mr: 1 }} /> Canceling...</> : 'Cancel Appointment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MobileServiceAppointmentDetails;
